import { Controller } from 'stimulus';
import { show as showToast } from '@/js/application_controllers/ui/toast_controller';

// Shows a keyboard-hint toast at most once per editor session. The hint appears the first
// time its trigger fires, then stays quiet for the rest of the session. If the user performs
// the action it describes (e.g. space + drag) it's silenced immediately — so someone who
// already knows the shortcut never sees it. State resets on reload.
//
// To add a new hint:
//   1. Add a <template> inside this controller's element:
//      <template data-moodboard--toast--key-action-target="hint" data-key-hint="my-hint">
//        <%= render(UI::Toast.new(...)) do |toast| %> ... <% end %>
//      </template>
//   2. Trigger it from wherever the interaction happens:
//      document.dispatchEvent(
//        new CustomEvent('moodboard--toast--key-action:show', { detail: { hint: 'my-hint' } })
//      );
//   3. Pre-empt it from wherever the user performs the described action:
//      document.dispatchEvent(
//        new CustomEvent('moodboard--toast--key-action:suppress', { detail: { hint: 'my-hint' } })
//      );
//
// Both events are wired declaratively via data-action on this controller's element.
export default class extends Controller {
  static targets = ['hint'];

  connect() {
    // Hints that should no longer appear this session — either already shown once or
    // pre-empted because the user performed the action they describe.
    this.silencedHints = new Set();
  }

  show(event) {
    const hint = event?.detail?.hint;
    if (!hint || this.silencedHints.has(hint)) {
      return;
    }

    const template = this.hintTargets.find((target) => target.dataset.keyHint === hint);
    if (!template) {
      return;
    }

    showToast(template);
    // Show each hint at most once per session.
    this.silencedHints.add(hint);
  }

  // Silence a hint for the rest of this session once the user has performed the action
  // it describes (e.g. space + drag), even if it was never shown.
  suppress(event) {
    const hint = event?.detail?.hint;
    if (hint) {
      this.silencedHints.add(hint);
    }
  }
}
